import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Paper } from "@mui/material";
import { BaseField } from "./BaseField";
import { BeforeMount, Editor, OnMount } from "@monaco-editor/react";
import SysColumn from "app/class/sys-column";
import { useCallback, useEffect, useMemo, useState } from "react";
import type * as monacoType from "monaco-editor";
import CodeFieldCustomErrorBoxComponent from "../record-producer-views/CodeFieldCustomErrorBox";
import { FieldRuntimeProps } from "modules/record/record-producer/types/record-producer-types";
import { buildRequiredRule, getRequiredFieldMessage } from "../../services/recordProducerValidation";
import { useRecordProducer } from "../RecordProducerContext";
import { useColumns } from "app/hooks/useColumns";
import { useTables } from "app/hooks/useTables";
import { resolveCodeEditorProfile } from "./code-editor/resolveCodeEditorProfile";
import { configureCodeEditor } from "./code-editor/configureCodeEditor";
import { useTranslation } from "react-i18next";
import { getFieldHelperText } from "../../services/fieldHelperText";
import { usePlatformTheme } from "app/providers/PlatformThemeProvider";
import { applyMonacoPlatformTheme } from "./code-editor/applyMonacoPlatformTheme";
import { codeEditorValue } from "modules/record/shared/services/recordQaBehavior";

interface CodeFieldComponentProps {
    column: SysColumn;
    runtimeProps?: FieldRuntimeProps;
}

const languageFromSubtype = (subtype?: string | null) => {
    const normalizedSubtype = String(subtype || "").trim().toLocaleLowerCase();

    switch (normalizedSubtype) {
        case "js":
        case "javascript":
            return "javascript";
        case "ts":
        case "typescript":
            return "typescript";
        case "html":
        case "xml":
            return "html";
        case "json":
            return "json";
        case "css":
            return "css";
        default:
            return normalizedSubtype || "plaintext";
    }
};

const extensionFromLanguage = (language: string) => {
    switch (language) {
        case "javascript":
            return "js";
        case "typescript":
            return "ts";
        case "html":
            return "html";
        case "json":
            return "json";
        case "css":
            return "css";
        default:
            return "txt";
    }
};

const configureLanguageDiagnostics = (monaco: typeof monacoType, language: string) => {
    if (language === "javascript" || language === "typescript") {
        const defaults = language === "typescript"
            ? monaco.languages.typescript.typescriptDefaults
            : monaco.languages.typescript.javascriptDefaults;

        defaults.setCompilerOptions({
            ...defaults.getCompilerOptions(),
            allowNonTsExtensions: true,
            checkJs: language === "javascript",
            noLib: false,
        });

        defaults.setDiagnosticsOptions({
            ...defaults.getDiagnosticsOptions(),
            noSemanticValidation: false,
            noSyntaxValidation: false,
        });
    }

    if (language === "html") {
        monaco.languages.html.htmlDefaults.setModeConfiguration({
            ...monaco.languages.html.htmlDefaults.modeConfiguration,
            diagnostics: true,
        });
    }

    if (language === "css") {
        monaco.languages.css.cssDefaults.setOptions({
            ...monaco.languages.css.cssDefaults.options,
            validate: true,
        });
    }
};

export const CodeFieldComponent = ({ column, runtimeProps }: CodeFieldComponentProps) => {
    const { clearErrors, control, setError } = useFormContext();
    const { t } = useTranslation();
    const { activeTheme } = usePlatformTheme();
    const recordProducer = useRecordProducer();
    const { data: allColumns = [] } = useColumns();
    const { data: allTables = [] } = useTables();
    const values = useWatch({ control });
    const [monacoInstance, setMonacoInstance] = useState<typeof monacoType | null>(null);
    const [editorHeight, setEditorHeight] = useState(120);

    const profile = useMemo(
        () => resolveCodeEditorProfile({
            table: recordProducer.table,
            column,
            values: (values ?? {}) as Record<string, unknown>,
            allTables,
            allColumns,
        }),
        [allColumns, allTables, column, recordProducer.table, values]
    );
    const editorLanguage = useMemo(
        () => profile?.language || languageFromSubtype(column.column_subtype),
        [column.column_subtype, profile?.language]
    );
    const editorPath = useMemo(
        () => `file:///primum/code-editor/models/${recordProducer.table.name}/${column.name}-${column.id}.${extensionFromLanguage(editorLanguage)}`,
        [column.id, column.name, editorLanguage, recordProducer.table.name]
    );

    const onMount: OnMount = useCallback((editor, monaco) => {
        configureLanguageDiagnostics(monaco, editorLanguage);
        setMonacoInstance(monaco);

        const updateEditorHeight = () =>
            setEditorHeight(Math.min(400, Math.max(120, editor.getContentHeight())));

        updateEditorHeight();
        editor.onDidContentSizeChange(updateEditorHeight);
    }, [editorLanguage]);

    const beforeMount: BeforeMount = useCallback((monaco) => {
        applyMonacoPlatformTheme(monaco, activeTheme);
        configureLanguageDiagnostics(monaco, editorLanguage);
    }, [activeTheme, editorLanguage]);

    useEffect(() => {
        if (!monacoInstance) {
            return;
        }

        applyMonacoPlatformTheme(monacoInstance, activeTheme);
    }, [activeTheme, monacoInstance]);

    useEffect(() => {
        if (!monacoInstance) {
            return undefined;
        }

        configureLanguageDiagnostics(monacoInstance, editorLanguage);

        const disposable = configureCodeEditor(monacoInstance, profile, editorPath, (key, fallback) =>
            t(key, { defaultValue: fallback || "" })
        );

        return () => disposable.dispose();
    }, [editorLanguage, editorPath, monacoInstance, profile, t]);

    return (
        <BaseField column={column} required={runtimeProps?.required} hidden={runtimeProps?.hidden} as="group">
            <Controller
                name={column.name}
                control={control}
                rules={{ required: buildRequiredRule(runtimeProps?.required ?? column.is_mandatory, getRequiredFieldMessage(column)) }}
                render={({ field, fieldState }) => (
                    <Paper
                        sx={{
                            borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                            backgroundColor: "background.paper",
                            border: "1px solid",
                            borderColor: "divider",
                            padding: "5px",
                            overflow: "hidden",
                        }}
                    >
                        <Editor
                            height={editorHeight}
                            language={editorLanguage}
                            path={editorPath}
                            beforeMount={beforeMount}
                            onMount={onMount}
                            theme="primum-platform-editor"
                            defaultValue={column.default_value}
                            value={codeEditorValue(field.value)}
                            onChange={(value) => field.onChange(value ?? "")}
                            onValidate={(codeErrors) => {
                                const relevantErrors = codeErrors
                                    .filter((error) => error.severity >= 4)
                                    .sort((first, second) => second.severity - first.severity);

                                if (relevantErrors.length > 0) {
                                    setError(column.name, { type: "monaco", message: relevantErrors[0].message || "Invalid code" });
                                    return;
                                }

                                clearErrors(column.name);
                            }}
                            options={{
                                minimap: { enabled: false },
                                fontSize: 14,
                                automaticLayout: true,
                                scrollBeyondLastLine: false,
                                wordWrap: "on",
                                readOnly: runtimeProps?.readonly,
                            }}
                        />
                        <CodeFieldCustomErrorBoxComponent fieldState={fieldState} helperText={getFieldHelperText(fieldState, runtimeProps)} />
                    </Paper>
                )}
            />
        </BaseField>
    );
};
